import React from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { FaSolarPanel, FaLeaf, FaTools, FaChartLine } from 'react-icons/fa';
import { BsLightningCharge } from 'react-icons/bs';
import { AiOutlineStar } from 'react-icons/ai';
import { HiOutlineClock } from 'react-icons/hi';
import { RiPlantLine } from 'react-icons/ri';

const AboutUs = () => {
    const location = {
        lat: 45.6566308,
        lng: 18.2177517
    };

    const mapContainerStyle = {
        width: '100%',
        height: '100%',
        borderRadius: '1rem',
    };

    const mapStyles = [
        {
            featureType: "all",
            elementType: "labels.text.fill",
            stylers: [{ color: "#4B5563" }]
        },
        {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: "#E2E8F0" }]
        },
        {
            featureType: "landscape",
            elementType: "geometry",
            stylers: [{ color: "#F3F4F6" }]
        },
        {
            featureType: "road",
            elementType: "geometry",
            stylers: [{ color: "#D1D5DB" }]
        },
        {
            featureType: "poi",
            elementType: "geometry",
            stylers: [{ color: "#E5E7EB" }]
        }
    ];

    const services = [
        { icon: <FaSolarPanel className="text-primary-500" />, text: 'Instalaciju solarnih panela' },
        { icon: <FaLeaf className="text-primary-500" />, text: 'Energetsko savjetovanje' },
        { icon: <FaTools className="text-primary-500" />, text: 'Održavanje solarnih sustava' },
        { icon: <FaChartLine className="text-primary-500" />, text: 'Projektiranje energetskih rješenja' }
    ];

    const stats = [
        { icon: <HiOutlineClock />, number: '3+', text: 'Višegodišnje iskustvo' },
        { icon: <AiOutlineStar />, number: '100+', text: 'Zadovoljnih klijenata' },
        { icon: <BsLightningCharge />, number: '3MW+', text: 'Instalirana snaga' },
        { icon: <RiPlantLine />, number: '100%', text: 'Predanost kvaliteti' }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-white to-primary-50 py-32 md:py-40">
            <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Left Column - Map */}
                <div className="relative min-h-[400px] lg:min-h-[calc(100vh-20rem)] animate-fade-in-left p-8">
                    <div className="sticky top-20 h-[calc(100vh-20rem)] w-full">
                        <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl 
                                      bg-white p-2 hover:shadow-primary-200/50 transition-shadow duration-300">
                            <LoadScript googleMapsApiKey="AIzaSyC47TpxmLPT64PvjJdEF_iXGszjy0J2EoU">
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={location}
                                    zoom={15}
                                    options={{
                                        styles: mapStyles,
                                        disableDefaultUI: true,
                                        zoomControl: false,
                                        mapTypeControl: false,
                                        streetViewControl: false,
                                        rotateControl: false,
                                        fullscreenControl: false,
                                        scrollwheel: false
                                    }}
                                >
                                    <Marker 
                                        position={location}
                                        animation={window.google?.maps.Animation.DROP}
                                    />
                                </GoogleMap>
                            </LoadScript>
                            
                            {/* Location Info Overlay */}
                            <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm
                                          p-4 rounded-lg shadow-lg animate-fade-in-up">
                                <h3 className="text-primary-600 font-semibold mb-1">Naša lokacija</h3>
                                <p className="text-gray-600 text-sm">
                                    Šljivoševci, Ul. Vladimira Nazora 62, Hrvatska
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column - Content */}
                <div className="px-8 lg:px-16 py-20 animate-fade-in-right">
                    <div className="max-w-xl mx-auto">
                        <h2 className="text-4xl font-bold mb-8 bg-gradient-to-r 
                                     from-primary-600 to-primary-800 bg-clip-text text-transparent
                                     animate-fade-in-down">
                            O nama
                        </h2>

                        <div className="space-y-6 text-gray-600 mb-12">
                            <p className="leading-relaxed animate-fade-in-up" 
                               style={{ animationDelay: '0.2s' }}>
                                Lux Energy je vodeća tvrtka u Hrvatskoj specijalizirana za solarna 
                                rješenja i održivu energiju. Naša misija je omogućiti pristupačnu 
                                i čistu energiju svim našim klijentima.
                            </p>
                            <p className="leading-relaxed animate-fade-in-up"
                               style={{ animationDelay: '0.3s' }}>
                                S višegodišnjim iskustvom u industriji, ponosimo se našim 
                                stručnim timom i vrhunskom uslugom. Specijalizirali smo se za:
                            </p>
                            <ul className="space-y-4 ml-6 animate-fade-in-up"
                                style={{ animationDelay: '0.4s' }}>
                                {services.map((service, index) => (
                                    <li key={index} 
                                        className="flex items-center group hover:translate-x-2 
                                                 transition-transform duration-300">
                                        <span className="w-8 h-8 flex items-center justify-center
                                                       bg-primary-100 rounded-lg mr-3 group-hover:scale-110 
                                                       transition-transform duration-300 text-xl">
                                            {service.icon}
                                        </span>
                                        <span className="text-gray-700">{service.text}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 gap-6 animate-fade-in-up"
                             style={{ animationDelay: '0.5s' }}>
                            {stats.map((stat, index) => (
                                <div key={index} 
                                     className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl 
                                              transition-all duration-300 group hover:-translate-y-1">
                                    <div className="text-3xl text-primary-500 mb-3 
                                                  group-hover:scale-110 transition-transform duration-300">
                                        {stat.icon}
                                    </div>
                                    <div className="text-2xl font-bold text-primary-600 mb-1">
                                        {stat.number}
                                    </div>
                                    <div className="text-gray-600 text-sm">{stat.text}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AboutUs;